import * as React from 'react';
import { cn } from '@/lib/utils';

export interface SwitchProps extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'onChange'
> {
    checked: boolean;
    onCheckedChange?: (checked: boolean) => void;
}

/**
 * A minimal, accessible on/off toggle (`role="switch"`). Drop-in for the
 * Checkbox API (`checked` / `onCheckedChange`) — used where a value is a live
 * show/hide rather than a form selection. Pure Tailwind, no extra dependency.
 */
const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
    ({ className, checked, onCheckedChange, disabled, ...props }, ref) => (
        <button
            ref={ref}
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => onCheckedChange?.(!checked)}
            className={cn(
                'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
                checked ? 'bg-primary' : 'bg-input',
                className,
            )}
            {...props}
        >
            <span
                className={cn(
                    'pointer-events-none block size-4 rounded-full bg-white shadow-sm ring-0 transition-transform',
                    checked ? 'translate-x-4' : 'translate-x-0',
                )}
            />
        </button>
    ),
);
Switch.displayName = 'Switch';

export { Switch };
